import { getValidAccessToken, refreshAccessToken } from './auth';
import { feature, featureForCall } from './telemetry';
import type {
  ContractState, ContractTemplate, ContractTree, FamilyLoanTerms, GenerationTargets,
  WorkItem,
} from '../types';

import { useAuthStore } from '../store/useAuthStore';
import type { DesignStandard } from './waysApi';

/** Error carrying the response status and parsed body.
 *
 *  Callers that need more than a sentence — the workflow engine's 409 returns
 *  `allowed` targets, for instance — read `detail` rather than parsing the
 *  message string.
 */
export class ApiError extends Error {
  status: number;
  detail: Record<string, any>;
  constructor(message: string, status: number, detail: Record<string, any>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail || {};
  }
}
const BASE_URL = '/api';

/**
 * Download a file from an authenticated endpoint.
 *
 * A plain `<a href="/api/...">` cannot be used for these: the browser navigates
 * directly and sends no Authorization header, because the token lives in JS
 * rather than a cookie. The response is then a 401 rendered as JSON in the tab,
 * which looks like a broken endpoint rather than a missing header.
 *
 * So fetch it with the token, turn the response into a blob, and hand that to a
 * temporary object URL.
 */
/**
 * Open a protected file in a new tab.
 *
 * Fetched with the token and handed to the tab as a blob URL, for the same
 * reason downloads are: a plain link navigates without the Authorization
 * header and renders the 401 as JSON. The object URL is revoked on a delay
 * rather than immediately — revoking it before the new tab has read it leaves
 * a blank window, which looks like the file is missing.
 */
export async function openFile(endpoint: string): Promise<void> {
  const token = await getValidAccessToken();
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let message = `Could not open the file (${res.status})`;
    try {
      const body = await res.json();
      message = body?.message || body?.error || message;
    } catch { /* not JSON; keep the status */ }
    throw new Error(message);
  }
  const url = URL.createObjectURL(await res.blob());
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (!win) {
    URL.revokeObjectURL(url);
    throw new Error('The browser blocked the new tab. Allow pop-ups for this site.');
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function downloadFile(endpoint: string, fallbackName: string): Promise<void> {
  const token = await getValidAccessToken();
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let message = `Download failed (${res.status})`;
    try {
      const body = await res.json();
      message = body.message || body.error || message;
    } catch {
      /* a non-JSON error body is not worth a second failure */
    }
    throw new Error(message);
  }

  // Prefer the name the server chose — it carries the template id and version.
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const name = match ? decodeURIComponent(match[1]) : fallbackName;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick: revoking synchronously can cancel the download
  // in some browsers before it has started reading the blob.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${BASE_URL}${endpoint}`;

  // Inject auth token if available
  const token = await getValidAccessToken();
  const authHeaders: Record<string, string> = {};
  if (token) {
    authHeaders['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options?.headers,
    },
    ...options,
  });

  // Handle auth errors — attempt token refresh + retry before logging out
  if (res.status === 401) {
    const newToken = await refreshAccessToken();

    if (newToken) {
      // Retry the original request with the fresh token
      const retryRes = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${newToken}`,
          ...options?.headers,
        },
        ...options,
      });

      if (retryRes.ok) {
        feature(featureForCall(options?.method, endpoint, options?.body));
        return retryRes.json();
      }

      // If retry also fails with 401, the user is truly unauthenticated
      if (retryRes.status === 401) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        throw new Error('Session expired. Please log in again.');
      }

      if (!retryRes.ok) {
        const body = await retryRes.json().catch(() => ({ error: retryRes.statusText }));
        throw new ApiError(
          body.message || body.error || `Request failed: ${retryRes.status}`,
          retryRes.status,
          body,
        );
      }
    }

    // Refresh failed — log out
    useAuthStore.getState().logout();
    window.location.href = '/login';
    throw new Error('Session expired. Please log in again.');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    // Prefer `message` over `error`: several endpoints put a machine-readable
    // slug in `error` (invalid_transition, insufficient_role) and the sentence
    // worth showing a human in `message`.
    const err = new ApiError(
      body.message || body.error || `Request failed: ${res.status}`,
      res.status,
      body,
    );
    throw err;
  }

  feature(featureForCall(options?.method, endpoint, options?.body));
  return res.json();
}

// ---- Projects ----
// Ways runner sessions (PO92-222/225): a project's owner switches runners on, with limits.
export interface RunnerEnablement {
  projectId: string;
  enabled: boolean;
  mode: 'on-demand' | 'standby';
  maxConcurrent: number;
  dailyTokenBudget: number | null;
  requestKinds: string[];
  push: boolean;
  approvedBy?: string | null;
  approvedAt?: string | null;
}

export const runnerApi = {
  get: (projectId: string) =>
    request<RunnerEnablement>(`/runner/enablement?projectId=${encodeURIComponent(projectId)}`),
  set: (data: Omit<RunnerEnablement, 'approvedBy' | 'approvedAt'>) =>
    request<RunnerEnablement>('/runner/enablement', { method: 'PUT', body: JSON.stringify(data) }),
};

export const projectApi = {
  /** Operations containers (isOperationsProject) are left out unless `includeOperations` (PO46-1262). */
  list: (opts: { includeOperations?: boolean } = {}) =>
    request<any[]>(opts.includeOperations ? '/projects?includeOperations=1' : '/projects'),
  get: (id: string) => request<any>(`/projects/${id}`),
  create: (data: any) => request<any>('/projects', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request<any>(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/projects/${id}`, { method: 'DELETE' }),
  getWorkItems: (id: string, workItemType?: string) => {
    const params = workItemType ? `?workItemType=${workItemType}` : '';
    return request<any[]>(`/projects/${id}/workitems${params}`);
  },
  promoteToProgram: (id: string, name: string) =>
    request<{ program: any; project: any }>(`/projects/${id}/promote-to-program`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
};

// ---- Work Items ----
/** Work item checkout: one holder at a time, a batch all or nothing (EP-28). */
export const checkoutApi = {
  checkout: (ids: string[], opts: { leaseHours?: number; reason?: string; includeChildren?: boolean } = {}) =>
    request<{ batch: string; ids: string[]; until: string }>('/workitems/checkout', { method: 'POST', body: JSON.stringify({ ids, ...opts }) }),
  renew: (ids: string[], leaseHours?: number) =>
    request<{ ids: string[]; until: string }>('/workitems/checkout/renew', { method: 'POST', body: JSON.stringify({ ids, leaseHours }) }),
  checkin: (ids: string[]) => request<{ ids: string[] }>('/workitems/checkin', { method: 'POST', body: JSON.stringify({ ids }) }),
  release: (ids: string[], reason: string) =>
    request<{ released: any[] }>('/workitems/checkout/release', { method: 'POST', body: JSON.stringify({ ids, reason }) }),
  list: (holder: 'me' | 'any' = 'me') => request<any[]>(`/workitems/checkouts?holder=${holder}`),
  /** Every live checkout the caller may read, with who is working it (hs-pmo/ep19#43). */
  live: (worked = false) =>
    request<{ at: string; cursor: string; items: LiveCheckout[] }>(`/workitems/checkouts/live${worked ? '?worked=1' : ''}`),
  /** Checkouts taken, renewed and released after `since` (the previous call's cursor). */
  changes: (since: string, limit = 200) =>
    request<{ events: CheckoutChange[]; cursor: string; more: boolean }>(
      `/workitems/checkouts/changes?since=${encodeURIComponent(since)}&limit=${limit}`),
};

export interface CheckoutHolder {
  kind: 'user' | 'token' | 'runner' | string;
  id?: string;
  email?: string;
  label?: string;
  actsFor?: string | null;
}

export interface LiveCheckout {
  id: string;
  displayId?: string;
  key?: string;
  title?: string;
  projectId?: string;
  status?: string;
  checkout: { holder: CheckoutHolder; since?: string; until: string; reason?: string; batch?: string;
    takenBy?: string; renewedAt?: string };
  /** runner (a Ways runner's grant), agent (an agent session active on it), token, or person. */
  holderKind: 'runner' | 'agent' | 'token' | 'person';
  runnerSession?: string | null;
  activeSessions: { id?: string; label?: string; kind?: string; agent?: boolean; lastSeen?: string }[];
  /** With ?worked=1 only. */
  lastWorked?: string | null;
  idle?: boolean;
}

export interface CheckoutChange {
  event: 'taken' | 'renewed' | 'released';
  action: string;
  item?: string;
  itemKey?: string;
  title?: string;
  projectId?: string;
  at: string;
  holder?: CheckoutHolder | { kind: 'runner'; runnerSession?: string };
}

export const workItemApi = {
  list: (filters?: Record<string, string>) => {
    const params = filters ? '?' + new URLSearchParams(filters).toString() : '';
    return request<any[]>(`/workitems${params}`);
  },
  get: (id: string) => request<any>(`/workitems/${id}`),
  // Resolve a work item from its human-readable hierarchical path, e.g.
  // ['PO-3','EP-1','FE-1','TS-1'] -> GET /workitems/path/PO-3/EP-1/FE-1/TS-1
  getByPath: (segments: string[]) =>
    request<any>(`/workitems/path/${segments.map(encodeURIComponent).join('/')}`),
  create: (data: any) => request<any>('/workitems', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request<any>(`/workitems/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/workitems/${id}`, { method: 'DELETE' }),
  updateStatus: (id: string, status: string) =>
    request<any>(`/workitems/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  reorder: (id: string, order: number, status?: string) =>
    request<any>(`/workitems/${id}/reorder`, { method: 'PUT', body: JSON.stringify({ order, status }) }),
  getChildren: (id: string) => request<any[]>(`/workitems/${id}/children`),
  // EP-6: cross-project Epic nesting
  getTree: (id: string, depth: number = 5) =>
    request<any>(`/workitems/${id}/tree?depth=${depth}`),
  getAncestry: (id: string) => request<any[]>(`/workitems/${id}/ancestry`),
  getPortfolioEpics: (rootProjectId?: string) => {
    const qs = rootProjectId ? `?rootProjectId=${encodeURIComponent(rootProjectId)}` : '';
    return request<any[]>(`/workitems/portfolio/epics${qs}`);
  },
};

// ---- Ideation: propose / triage / approve capabilities -----------
export const ideaApi = {
  // Ideas are excluded from the default list; pass workItemType=idea to fetch them.
  list: (projectId?: string) => {
    const params = new URLSearchParams({ workItemType: 'idea' });
    if (projectId) params.set('projectId', projectId);
    return request<any[]>(`/workitems?${params.toString()}`);
  },
  get: (id: string) => request<any>(`/workitems/${id}`),
  create: (data: any) =>
    request<any>('/workitems', { method: 'POST', body: JSON.stringify({ ...data, workItemType: 'idea' }) }),
  update: (id: string, data: any) =>
    request<any>(`/workitems/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  setStatus: (id: string, ideaStatus: string) =>
    request<any>(`/workitems/${id}/idea-status`, { method: 'PATCH', body: JSON.stringify({ ideaStatus }) }),
  approve: (
    id: string,
    body: {
      targetPiId?: string; sprintId?: string;
      parentEpicId?: string; createParentEpic?: boolean; newEpicTitle?: string;
      organizationId?: string; programId?: string;
    }
  ) => request<{ idea: any; realized: any; realizedKind?: string }>(`/workitems/${id}/idea-approve`, { method: 'POST', body: JSON.stringify(body) }),
};

// ---- EP-6: Portfolio parents governance -----------
export const portfolioApi = {
  listParents: (projectId: string) =>
    request<{ projectId: string; portfolioParentProjectIds: string[] }>(
      `/projects/${projectId}/portfolio-parents`,
    ),
  addParent: (projectId: string, parentProjectId: string) =>
    request<{ projectId: string; portfolioParentProjectIds: string[] }>(
      `/projects/${projectId}/portfolio-parents`,
      { method: 'POST', body: JSON.stringify({ parentProjectId }) },
    ),
  removeParent: (projectId: string, parentProjectId: string) =>
    request<{ projectId: string; portfolioParentProjectIds: string[] }>(
      `/projects/${projectId}/portfolio-parents/${encodeURIComponent(parentProjectId)}`,
      { method: 'DELETE' },
    ),
};

// ---- Analytics: counts without loading documents (EP-16) ----
export interface ProjectCounts {
  projectId: string;
  total: number;
  byType: Record<string, number>;
  byStatus: Record<string, number>;
  byTypeAndStatus: Record<string, Record<string, number>>;
  scheduling: { inSprint: number; noSprint: number; inRelease: number; noRelease: number };
  /** 'view' when served from the CouchDB reduce; 'documents' when it fell back. */
  source: 'view' | 'documents';
}

export const analyticsApi = {
  /** Counts for one project. Costs no document reads on the view-backed path. */
  forProject: (projectId: string) =>
    request<ProjectCounts>(`/analytics/counts?projectId=${encodeURIComponent(projectId)}`),
  /** Per-project totals for the whole estate — one grouped read, not a scan. */
  totals: () =>
    request<{ byProject: Record<string, number>; total: number; projectCount: number }>(
      '/analytics/counts'),
  byAssignee: (projectId: string) =>
    request<{ projectId: string; byAssignee: Record<string, number>; unassigned: number; total: number }>(
      `/analytics/counts/by-assignee?projectId=${encodeURIComponent(projectId)}`),
};

/** A page of work items. Requesting `limit` switches the list endpoint from a
 *  bare array to this envelope; omitting it preserves the old shape. */
export interface WorkItemPage {
  items: any[];
  total: number;
  offset: number;
  limit: number;
  hasMore: boolean;
}

export const workItemPageApi = {
  page: (filters: Record<string, string>, limit: number, offset = 0) => {
    const params = new URLSearchParams({ ...filters, limit: String(limit), offset: String(offset) });
    return request<WorkItemPage>(`/workitems?${params.toString()}`);
  },
};

// ---- Workflows (F6) ----
export const workflowApi = {
  list: (filters?: { entityType?: string; organizationId?: string }) => {
    const q = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => { if (v) q.set(k, v); });
    const qs = q.toString();
    return request<any[]>(`/workflows${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => request<any>(`/workflows/${id}`),
  create: (data: any) => request<any>('/workflows', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request<any>(`/workflows/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/workflows/${id}`, { method: 'DELETE' }),
  seed: () => request<Record<string, string>>('/workflows/seed', { method: 'POST' }),
  /** Projects bound to a workflow, and how many entities sit in each state. */
  usage: (id: string) =>
    request<{
      workflowId: string;
      entityType: string;
      entityCount: number;
      stateCounts: Record<string, number>;
      orphanedStates: string[];
      boundProjects: { id: string; name: string; isProjectDefault: boolean; boundTypes: string[] }[];
      canDelete: boolean;
      blockedReason: string;
    }>(`/workflows/${id}/usage`),
  /** What a board calls to build its columns. */
  resolve: (params: { projectId?: string; workItemType?: string; entityType?: string; workflowId?: string }) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
    return request<any>(`/workflows/resolve?${q.toString()}`);
  },
  /** Legal moves from an item's current state. */
  transitionsFor: (workItemId: string) =>
    request<{
      workflow: { _id: string; name: string; version: number } | null;
      current: string; category: string; allowed: string[]; states: any[];
    }>(`/workitems/${workItemId}/transitions`),
};

// ---- Comments ----
export const commentApi = {
  list: (workItemId: string) => request<any[]>(`/workitems/${workItemId}/comments`),
  create: (workItemId: string, data: { body: string; author?: string }) =>
    request<any>(`/workitems/${workItemId}/comments`, { method: 'POST', body: JSON.stringify(data) }),
  delete: (commentId: string) => request<void>(`/comments/${commentId}`, { method: 'DELETE' }),
};

// ---- Time Entries ----
export const timeEntryApi = {
  list: (workItemId: string) => request<any[]>(`/workitems/${workItemId}/time-entries`),
  create: (workItemId: string, data: { hours: number; date?: string; description?: string }) =>
    request<any>(`/workitems/${workItemId}/time-entries`, { method: 'POST', body: JSON.stringify(data) }),
  delete: (entryId: string) => request<void>(`/time-entries/${entryId}`, { method: 'DELETE' }),
};

// ---- Activities ----
export const activityApi = {
  list: (params?: { limit?: number; targetId?: string; targetType?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.targetId) searchParams.set('targetId', params.targetId);
    if (params?.targetType) searchParams.set('targetType', params.targetType);
    const qs = searchParams.toString();
    return request<any[]>(`/activities${qs ? '?' + qs : ''}`);
  },
};

// ---- Attachments ----
export const attachmentApi = {
  list: (workItemId: string) => request<any[]>(`/workitems/${workItemId}/attachments`),
  upload: async (workItemId: string, file: File, uploadedBy?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (uploadedBy) formData.append('uploadedBy', uploadedBy);

    const token = await getValidAccessToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${BASE_URL}/workitems/${workItemId}/attachments`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(error.error || 'Upload failed');
    }
    return res.json();
  },
  delete: (attachmentId: string) => request<void>(`/attachments/${attachmentId}`, { method: 'DELETE' }),
  downloadUrl: (attachmentId: string) => `${BASE_URL}/attachments/${attachmentId}/download`,
};

// ---- Config ----
export const configApi = {
  get: () => request<any>('/config'),
  update: (data: any) => request<any>('/config', { method: 'PUT', body: JSON.stringify(data) }),
  getStatuses: () => request<any[]>('/config/statuses'),
  getDashboard: () => request<any>('/config/dashboard'),
  updateDashboard: (data: any) =>
    request<any>('/config/dashboard', { method: 'PUT', body: JSON.stringify(data) }),
};

// ---- Funding Pipeline ----
export const fundingApi = {
  list: (filters?: Record<string, string>) => {
    const params = filters ? '?' + new URLSearchParams(filters).toString() : '';
    return request<any[]>(`/funding${params}`);
  },
  get: (id: string) => request<any>(`/funding/${id}`),
  create: (data: any) => request<any>('/funding', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request<any>(`/funding/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/funding/${id}`, { method: 'DELETE' }),
  summary: () => request<any>('/funding/summary'),
  createProject: (id: string) => request<any>(`/funding/${id}/create-project`, { method: 'POST' }),
  getDocuments: (id: string) => request<any[]>(`/funding/${id}/documents`),
  uploadDocument: async (fundingId: string, file: File, meta?: { uploadedBy?: string; documentType?: string; version?: string }) => {
    const formData = new FormData();
    formData.append('file', file);
    if (meta?.uploadedBy) formData.append('uploadedBy', meta.uploadedBy);
    if (meta?.documentType) formData.append('documentType', meta.documentType);
    if (meta?.version) formData.append('version', meta.version);

    const token = await getValidAccessToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${BASE_URL}/funding/${fundingId}/documents`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(error.error || 'Upload failed');
    }
    return res.json();
  },
};

// ---- Resources / Assignees ----
export const resourceApi = {
  list: (activeOnly?: boolean) => {
    const params = activeOnly ? '?active=true' : '';
    return request<any[]>(`/resources${params}`);
  },
  get: (id: string) => request<any>(`/resources/${id}`),
  create: (data: any) => request<any>('/resources', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request<any>(`/resources/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/resources/${id}`, { method: 'DELETE' }),
};

// ---- Funder CRM ----
export const funderApi = {
  list: (filters?: Record<string, string>) => {
    const params = filters ? '?' + new URLSearchParams(filters).toString() : '';
    return request<any[]>(`/funders${params}`);
  },
  get: (id: string) => request<any>(`/funders/${id}`),
  create: (data: any) => request<any>('/funders', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request<any>(`/funders/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/funders/${id}`, { method: 'DELETE' }),
  addInteraction: (id: string, data: any) => request<any>(`/funders/${id}/interactions`, { method: 'POST', body: JSON.stringify(data) }),
  getOpportunities: (id: string) => request<any[]>(`/funders/${id}/opportunities`),
};

// ---- Users (Admin) ----
export const userApi = {
  list: (activeOnly?: boolean) => {
    const params = activeOnly ? '?active=true' : '';
    return request<any[]>(`/users${params}`);
  },
  get: (id: string) => request<any>(`/users/${id}`),
  /** Display names for hs-auth subjects ("user:<id>", as Ways names people); unknown ones are absent. */
  names: (subs: string[]) =>
    request<{ names: Record<string, string> }>(`/users/names?subs=${encodeURIComponent(subs.join(','))}`),
  create: (data: { email: string; name: string; role: string }) =>
    request<any>('/users', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request<any>(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateRole: (id: string, role: string) =>
    request<any>(`/users/${id}/role`, { method: 'PUT', body: JSON.stringify({ role }) }),
  assignProjects: (id: string, projectIds: string[]) =>
    request<any>(`/users/${id}/projects`, { method: 'PUT', body: JSON.stringify({ projectIds }) }),
  deactivate: (id: string) => request<any>(`/users/${id}`, { method: 'DELETE' }),
};

// ---- Organizations ----
export const organizationApi = {
  list: () => request<any[]>('/organizations'),
  get: (id: string) => request<any>(`/organizations/${id}`),
  create: (data: any) => request<any>('/organizations', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request<any>(`/organizations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/organizations/${id}`, { method: 'DELETE' }),
  getTree: (id: string) => request<any>(`/organizations/${id}/tree`),
};

// ---- Documentation (CouchDB-backed markdown docs) ----
export const docsApi = {
  list: (category?: string) => request<any[]>(`/docs${category ? `?category=${encodeURIComponent(category)}` : ''}`),
  get: (id: string) => request<any>(`/docs/${id}`),
  create: (data: any) => request<any>('/docs', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request<any>(`/docs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/docs/${id}`, { method: 'DELETE' }),
};

// ---- The design standard on the Ways rail (hs-pmo/ep43#12) ----
// PMO proxies harness-api, which holds the standard and its versions. `available: false`
// means the harness store is not live yet; the page then shows what Ways serves (the seed).
export interface DesignStandardVersion { version: string; savedAt?: string; savedBy?: string; note?: string }

export type DesignStandardCurrent =
  | { available: false; reason?: string }
  | { available: true; source: 'harness'; version?: string; standard: DesignStandard; canEdit: boolean; editRefusal?: string | null };

export const designStandardApi = {
  current: () => request<DesignStandardCurrent>('/ways/design-standard'),
  versions: () => request<{ available: boolean; versions: DesignStandardVersion[]; reason?: string }>('/ways/design-standard/versions'),
  version: (v: string) =>
    request<{ source: 'harness'; version: string; standard: DesignStandard }>(`/ways/design-standard/versions/${encodeURIComponent(v)}`),
  /** Owner only (Zane): a super_admin the standard names as its owner. A stale baseVersion is a 409. */
  save: (standard: DesignStandard, baseVersion: string | undefined, note: string) =>
    request<{ version?: string }>('/ways/design-standard', {
      method: 'PUT', body: JSON.stringify({ standard, baseVersion, note }),
    }),
};

// ---- RBAC (roles, groups, project groups, grants, memberships) ----
export const rbacApi = {
  vocabulary: () => request<{ permissions: string[]; modules: string[]; standardRoles: string[] }>('/rbac/vocabulary'),
  // roles
  listRoles: (orgId?: string) => request<any[]>(`/rbac/roles${orgId ? `?orgId=${encodeURIComponent(orgId)}` : ''}`),
  createRole: (data: any) => request<any>('/rbac/roles', { method: 'POST', body: JSON.stringify(data) }),
  updateRole: (id: string, data: any) => request<any>(`/rbac/roles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteRole: (id: string) => request<void>(`/rbac/roles/${id}`, { method: 'DELETE' }),
  // memberships
  listMemberships: (filters?: Record<string, string>) =>
    request<any[]>(`/rbac/memberships${filters ? '?' + new URLSearchParams(filters).toString() : ''}`),
  createMembership: (data: any) => request<any>('/rbac/memberships', { method: 'POST', body: JSON.stringify(data) }),
  updateMembership: (id: string, data: any) => request<any>(`/rbac/memberships/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteMembership: (id: string) => request<void>(`/rbac/memberships/${id}`, { method: 'DELETE' }),
  // user groups
  listUserGroups: (orgId?: string) => request<any[]>(`/rbac/usergroups${orgId ? `?orgId=${encodeURIComponent(orgId)}` : ''}`),
  createUserGroup: (data: any) => request<any>('/rbac/usergroups', { method: 'POST', body: JSON.stringify(data) }),
  updateUserGroup: (id: string, data: any) => request<any>(`/rbac/usergroups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUserGroup: (id: string) => request<void>(`/rbac/usergroups/${id}`, { method: 'DELETE' }),
  // project groups
  listProjectGroups: (orgId?: string) => request<any[]>(`/rbac/projectgroups${orgId ? `?orgId=${encodeURIComponent(orgId)}` : ''}`),
  createProjectGroup: (data: any) => request<any>('/rbac/projectgroups', { method: 'POST', body: JSON.stringify(data) }),
  updateProjectGroup: (id: string, data: any) => request<any>(`/rbac/projectgroups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProjectGroup: (id: string) => request<void>(`/rbac/projectgroups/${id}`, { method: 'DELETE' }),
  // grants
  listGrants: (filters?: Record<string, string>) =>
    request<any[]>(`/rbac/grants${filters ? '?' + new URLSearchParams(filters).toString() : ''}`),
  createGrant: (data: any) => request<any>('/rbac/grants', { method: 'POST', body: JSON.stringify(data) }),
  deleteGrant: (id: string) => request<void>(`/rbac/grants/${id}`, { method: 'DELETE' }),
  // effective (informational)
  effective: (userId: string) => request<any>(`/rbac/effective/${userId}`),
  // access explorer — resolved access and the gate trace behind a decision
  effectiveToken: (tokenId: string) => request<any>(`/rbac/effective/token/${tokenId}`),
  effectiveUserResolved: (userId: string) => request<any>(`/rbac/effective/user/${userId}/resolved`),
  explain: (body: {
    subjectKind: string; subjectId: string; permission: string;
    projectId?: string; method?: string; path?: string; workItemType?: string;
  }) => request<any>('/rbac/explain', { method: 'POST', body: JSON.stringify(body) }),
  accessWarnings: () => request<{ items: any[]; total: number }>('/rbac/access-warnings'),
  // token wizard — SCOPE x ROLE vocabulary and a pre-save preview. Served by the
  // API rather than duplicated here: the wizard exists to prevent illegal or
  // surprising combinations, so it must read the table the server enforces.
  tokenVocabulary: () => request<any>('/rbac/token-vocabulary'),
  tokenPreview: (body: any) => request<any>('/rbac/token-preview', { method: 'POST', body: JSON.stringify(body) }),
  createTokenViaWizard: (body: any) => request<any>('/api-tokens', { method: 'POST', body: JSON.stringify(body) }),
};

// ---- Programs ----
export const programApi = {
  list: (organizationId?: string) => {
    const params = organizationId ? `?organizationId=${organizationId}` : '';
    return request<any[]>(`/programs${params}`);
  },
  get: (id: string) => request<any>(`/programs/${id}`),
  create: (data: any) => request<any>('/programs', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request<any>(`/programs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/programs/${id}`, { method: 'DELETE' }),
};

// ---- Release Trains ----
export const releaseTrainApi = {
  list: (programId?: string) => {
    const params = programId ? `?programId=${programId}` : '';
    return request<any[]>(`/releasetrains${params}`);
  },
  get: (id: string) => request<any>(`/releasetrains/${id}`),
  create: (data: any) => request<any>('/releasetrains', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request<any>(`/releasetrains/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/releasetrains/${id}`, { method: 'DELETE' }),
};

// ---- API Tokens (Admin) ----
export const apiTokenApi = {
  list: (activeOnly?: boolean) => {
    const params = activeOnly ? '?active=true' : '';
    return request<any[]>(`/api-tokens${params}`);
  },
  get: (id: string) => request<any>(`/api-tokens/${id}`),
  create: (data: { name: string; description?: string; accountType?: string; projectId?: string; projectIds?: string[]; ownerUserId: string; permissions?: string[]; allowedTypes?: string[] }) =>
    request<any>('/api-tokens', { method: 'POST', body: JSON.stringify(data) }),
  // Your OWN training token (EP-26 FE-16 ST-1): any member of the programme's training groups;
  // the server makes you the owner whatever is sent.
  issueMyTrainingToken: (programId: string, name: string, expiresInDays: number) =>
    request<any>('/api-tokens', { method: 'POST', body: JSON.stringify({ kind: 'training', programId, name, expiresInDays }) }),
  update: (id: string, data: any) => request<any>(`/api-tokens/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  regenerate: (id: string) => request<any>(`/api-tokens/${id}/regenerate`, { method: 'POST' }),
  reveal: (id: string) => request<any>(`/api-tokens/${id}/reveal`),
  revoke: (id: string) => request<any>(`/api-tokens/${id}/revoke`, { method: 'POST' }),
  delete: (id: string) => request<void>(`/api-tokens/${id}`, { method: 'DELETE' }),
};

// ---- Dashboard Config ----
export const dashboardConfigApi = {
  get: () => request<any>('/dashboard-config'),
  update: (data: any) => request<any>('/dashboard-config', { method: 'PUT', body: JSON.stringify(data) }),
  /** Record that the user opened a project. Debounced server-side: re-opening
   *  the project that is already most recent writes nothing. */
  recordRecentProject: (projectId: string) =>
    request<{ lastProjectId: string; recentProjectIds: string[] }>(
      '/dashboard-config/recent-project',
      { method: 'POST', body: JSON.stringify({ projectId }) }),
};

// ---- CRM ----
/** Lead as the management API flattens it. Survives CRM FE-1 promoting `lead`
 *  to a work item type — the backend reads both shapes. */
export interface Lead {
  id: string;
  displayId: string;
  title: string;
  status: string;
  projectId: string;
  createdAt: string;
  updatedAt: string;
  assignee: string;
  name: string;
  email: string;
  organisation: string;
  message: string;
  source: string;
  page: string;
  referrer: string;
  utm: Record<string, string>;
  /** Repeat enquiries attached to this lead rather than filed separately. */
  submissionCount?: number;
  submissions?: { receivedAt: string; message: string; subject: string; source: string; page: string }[];
  disqualifiedReason?: string;
  convertedToId?: string;
  workItemType?: string;
}

export interface LeadList {
  leads: Lead[];
  total: number;
  projectIds: string[];
  sources: string[];
  typed: boolean;
}

/** NOT `/leads` — that prefix is the public intake path behind an IP allowlist
 *  and is unreachable from inside the network. */
export interface Opportunity {
  id: string;
  displayId: string;
  title: string;
  status: string;
  projectId: string;
  createdAt: string;
  updatedAt: string;
  assignee: string;
  account: string;
  clientId: string;
  /** The reseller this deal is sold through, by `orgId`. */
  partnerOrgId: string;
  /** Which of OUR companies sells it — a `parent` account's `orgId`. '' on
   *  deals filed before the seller existed; those show under every seller. */
  sellerOrgId: string;
  contactName: string;
  contactEmail: string;
  source: string;
  /** Minor units. Format at the edge; never do arithmetic on a formatted string. */
  valueMinor: number;
  currency: string;
  region: string;
  probability: number;
  expectedCloseDate: string;
  sourceLeadId: string;
  lostReason: string;
}

export interface OpportunityList {
  opportunities: Opportunity[];
  total: number;
  projectIds: string[];
  sources: string[];
  /** Open deals only — won and lost are excluded. */
  pipelineValueMinor: number;
  weightedValueMinor: number;
}

export interface ConversionPreview {
  leadId: string;
  alreadyConverted: boolean;
  convertedToId: string;
  proposal: Record<string, any>;
}

export interface CrmContact { name?: string; email?: string; role?: string; }

export interface CrmBillingContact { name?: string; email?: string; phone?: string }

/** A frozen month of Research usage (crmusagerecord). Quantities and prices are decimal strings. */
export interface CrmUsageRecord {
  _id: string;
  period: string;
  quantity: string;
  includedAu?: string;
  unitPrice: string;
  currency: string;
  rateCardVersion?: string;
  lines?: { framework?: string; dimension?: string; subdimension?: string; au?: number; files?: number; analyses?: number }[];
  invoiceId?: string;
  invoice?: { _id?: string; number?: string; status?: string; totalMinor?: number; currency?: string } | null;
}

export interface CrmClient {
  _id: string;
  name: string;
  industry: string;
  website: string;
  /** PRICING region ('US', 'GB', 'ZA'). Not a residency boundary. */
  regionCode: string;
  projectId: string;
  contacts: CrmContact[];
  notes: string;
  /** The estate's tenancy key. Immutable once allocated. */
  orgId?: string;
  /** OWNERSHIP — the group this account belongs to. Not the reseller. */
  parentOrgId?: string;
  /**
   * SERVICE — the reseller that sells and services this account, by `orgId`.
   *
   * A DIFFERENT relationship from `parentOrgId` and not derivable from it: an
   * account can belong to one group and be sold by a reseller outside it. Also
   * distinct from an opportunity's `oppPartnerOrgId`, which is the write-once
   * execution record for ONE deal — this is the standing fact the deal defaults
   * from, and unlike the deal binding it stays editable.
   */
  partnerOrgId?: string;
  /** Position in the reseller hierarchy. '' predates the typed model. */
  orgType?: 'parent' | 'reseller' | 'client' | '';
  /**
   * DATA RESIDENCY ('south-africa' | 'europe' | 'usa'), a different axis from
   * `regionCode` — a price list must not decide where data may live.
   */
  region?: string;
  status?: string;
  displayId?: string;
  /** What the RBAC mirror did on the last save. Present on create/update only. */
  orgSync?: { orgId: string; organizationId: string; changed: string[]; error: string };
  /** What a quote or invoice must say about the company. Blank = not yet known. */
  registration?: CrmRegistration;
  /** How customers pay us. Only on our own (parent) companies; printed on their invoices. */
  remittance?: CrmRemittance;
  /** Who receives billing; Research reads it by orgId to grant billing-only access (EP-44 FE-2 ST-3). */
  billingContact?: CrmBillingContact;
  /** Net N days. Our companies' default invoice terms. */
  paymentTermsDays?: number | null;
}

/** One account's CRM record set against its RBAC organisation. */
export interface OrgDivergence {
  orgId: string;
  name: string;
  organizationId?: string;
  issue?: string;
  fields?: Record<string, {
    crm: string;
    rbac: string;
    expected: string;
    /**
     * The access model holds this edge and the CRM has never been asked about
     * it — research established it before the CRM had the field. Not a
     * disagreement: the sync ADOPTS it onto the account rather than clearing it.
     */
    adoptable?: boolean;
  }>;
}

export interface CrmProduct {
  _id: string;
  sku: string;
  catalogId: string;
  familyId: string;
  familyName: string;
  name: string;
  description: string;
  /** Minor units. */
  basePriceMinor: number;
  costMinor: number;
  msrpMinor: number;
  billingPeriod: string;
  unit: string;
  volumetric: boolean;
  active: boolean;
  familyType: string;
  /** Set when the SKU was withdrawn from sale; '' while it is on sale. */
  deactivatedAt?: string;
}

export interface CrmRegion {
  _id: string;
  code: string;
  name: string;
  currency: string;
  currencySymbol: string;
  colIndex: number;
  priceOffsetPct: number;
  /** An FX rate is not money and stays a float. */
  exchangeRate: number;
}

export interface CrmSettings {
  discountGovernance: Record<string, any>;
  discountSchedule: Record<string, any>;
}

export interface QuoteLineInput {
  sku: string;
  quantity: number;
  discountPct?: number;
  /** Ad-hoc lines only; a catalog SKU always prices from the catalog. */
  basePriceMinor?: number;
  name?: string;
}

export interface QuoteLine extends QuoteLineInput {
  adHoc: boolean;
  unitPriceMinor: number;
  grossMinor: number;
  discountMinor: number;
  netMinor: number;
  regionalOffsetPct: number;
  discountPct: number;
}

export interface CrmRegistration {
  registeredName?: string;
  companyType?: string;
  registrationNumber?: string;
  registrationAuthority?: string;
  jurisdiction?: string;
  taxIdLabel?: string;
  taxId?: string;
  registeredAddress?: string;
  billingAddress?: string;
  verified?: boolean;
}

export interface CrmRemittance {
  bankName?: string; accountName?: string; accountNumber?: string; routingNumber?: string;
  sortCode?: string; iban?: string; swift?: string; bankAddress?: string; paymentReference?: string;
}

/** A file on an opportunity — a work-item attachment tagged with what it is. */
export interface CrmDocument {
  _id: string;
  filename: string;
  size: number;
  createdAt: string;
  uploadedBy: string;
  crmKind?: 'signed_quote' | 'purchase_order' | 'sow' | 'other';
  quoteId?: string;
  poNumber?: string;
}

export interface CrmInvoice {
  _id: string;
  status: 'draft' | 'issued' | 'paid' | 'void';
  /** '' until issued — numbers are allocated at issue, per issuing company. */
  number: string;
  quoteId: string;
  quoteNumber: string;
  quoteVersion: number;
  opportunityId: string;
  sellerOrgId: string;
  billToOrgId: string;
  billToIsPartner: boolean;
  poNumber: string;
  termsDays: number;
  issueDate: string;
  dueDate: string;
  totalMinor: number;
  currency: string;
  notes: string;
  paidOn?: string;
  paymentReference?: string;
  voidReason?: string;
  createdAt: string;
}

/** A milestone on a quote: what lands, and when ("Week 4", "2027-01-15"). */
export interface QuoteScopeItem { text: string; target?: string; }

/** Mirrors MAX_SCOPE_ITEMS in crm_quote_service.py, which enforces it. */
export const MAX_SCOPE_ITEMS = 8;

export interface CrmQuote {
  _id: string;
  quoteNumber: string;
  quoteNumberSeq: number;
  version: number;
  status: 'draft' | 'issued' | 'accepted' | 'rejected' | 'expired';
  supersededById: string;
  opportunityId: string;
  clientId: string;
  /** Captured from the opportunity: who issues the quote, and who it is sold through. */
  sellerOrgId?: string;
  partnerOrgId?: string;
  title: string;
  notes: string;
  /** Major milestones, unpriced. Never part of a total. At most 8. */
  scopeItems?: QuoteScopeItem[];
  /** The Statement of Work this quote is scoped by — a reference, not a copy. */
  sowRef?: string;
  /** The client's purchase order number, printed on the acceptance block. */
  poNumber?: string;
  /** The signed copy that accepted it, and the Statement of Work — contract-store attachments. */
  signedDocumentId?: string;
  signedFilename?: string;
  sowDocumentId?: string;
  sowFilename?: string;
  validUntil: string;
  approvedBy: string;
  approvedAt: string;
  issuedAt: string;
  lines: QuoteLine[];
  regionCode: string;
  regionalOffsetPct: number;
  // All minor units, all integers.
  grossMinor: number;
  lineDiscountMinor: number;
  scheduleDiscountMinor: number;
  scheduleDiscountPct: number;
  scheduleRules: { id: string; discountPct: number; note: string }[];
  totalDiscountMinor: number;
  totalDiscountPct: number;
  discountCapped: boolean;
  discountCapPct: number;
  netMinor: number;
  taxRatePct: number;
  taxMinor: number;
  totalMinor: number;
  currency: string;
  localCurrency: string;
  exchangeRate: number;
  totalLocalMinor: number;
  requiresApproval: boolean;
  violations: string[];
}

export const crmApi = {
  listLeads: (params?: { projectId?: string; source?: string; status?: string }) => {
    const q = new URLSearchParams(
      Object.entries(params || {}).filter(([, v]) => v) as [string, string][]
    ).toString();
    return request<LeadList>(`/crm/leads${q ? `?${q}` : ''}`);
  },
  getLead: (id: string) => request<Lead>(`/crm/leads/${id}`),

  /** Manual entry. 409 when an open lead already exists; resend with force. */
  createLead: (data: Record<string, any>) =>
    request<Lead>('/crm/leads', { method: 'POST', body: JSON.stringify(data) }),
  checkDuplicateLead: (email: string, projectId?: string) =>
    request<{ duplicate: boolean; lead?: Lead }>(
      `/crm/leads/check-duplicate?email=${encodeURIComponent(email)}` +
      (projectId ? `&projectId=${encodeURIComponent(projectId)}` : '')),
  /** Dry run unless apply is true. Reports per-row outcomes. */
  importLeads: (data: { projectId: string; rows: any[]; apply?: boolean }) =>
    request<any>('/crm/leads/import', { method: 'POST', body: JSON.stringify(data) }),

  listOpportunities: (params?: {
    projectId?: string; source?: string; status?: string; sellerOrgId?: string;
  }) => {
    const q = new URLSearchParams(
      Object.entries(params || {}).filter(([, v]) => v) as [string, string][]
    ).toString();
    return request<OpportunityList>(`/crm/opportunities${q ? `?${q}` : ''}`);
  },
  getOpportunity: (id: string) => request<Opportunity>(`/crm/opportunities/${id}`),
  /** A deal with no lead behind it. The server checks the organisations named. */
  createOpportunity: (data: Record<string, any>) =>
    request<Opportunity>('/crm/opportunities', { method: 'POST', body: JSON.stringify(data) }),
  /** Commercial fields only (opp*, title, description, assignee). Stages move
   *  through the pipeline, not here. */
  updateOpportunity: (id: string, data: Record<string, any>) =>
    request<Opportunity>(`/crm/opportunities/${id}`,
      { method: 'PATCH', body: JSON.stringify(data) }),

  /** Propose: what conversion would create, without creating it. */
  previewConversion: (leadId: string) =>
    request<ConversionPreview>(`/crm/leads/${leadId}/convert`),
  /** Commit: the reviewer's edits override the proposal. */
  convertLead: (leadId: string, overrides: Record<string, any>) =>
    request<{ ok: boolean; opportunity: Opportunity; leadId: string }>(
      `/crm/leads/${leadId}/convert`,
      { method: 'POST', body: JSON.stringify(overrides) }),

  // ---- clients, catalog, regions (FE-6) ----
  listClients: (q?: string, orgType?: string, opts?: {
    partnerOrgId?: string; includeArchived?: boolean;
  }) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    // `reseller` is the CRM's word for what research calls the partner.
    if (orgType) params.set('orgType', orgType);
    if (opts?.partnerOrgId) params.set('partnerOrgId', opts.partnerOrgId);
    // Archived accounts are excluded server-side by default so they drop out of
    // every picker without each picker having to remember.
    if (opts?.includeArchived) params.set('includeArchived', 'true');
    const qs = params.toString();
    return request<{ clients: CrmClient[]; total: number; industries: string[] }>(
      `/crm/clients${qs ? `?${qs}` : ''}`);
  },
  getClient: (id: string) => request<CrmClient>(`/crm/clients/${id}`),
  /** Monthly Research usage records for a client, newest first, each with its invoice (EP-44 FE-2). */
  clientUsage: (id: string) => request<{ records: CrmUsageRecord[]; total: number }>(`/crm/clients/${id}/usage`),
  createClient: (data: Partial<CrmClient>) =>
    request<CrmClient>('/crm/clients', { method: 'POST', body: JSON.stringify(data) }),
  updateClient: (id: string, data: Partial<CrmClient>) =>
    request<CrmClient>(`/crm/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  /** Withdraw an account from use without removing it. The default over delete. */
  archiveClient: (id: string, active = false) =>
    request<CrmClient>(`/crm/clients/${id}/archive`,
      { method: 'POST', body: JSON.stringify({ active }) }),
  deleteClient: (id: string, force = false) =>
    request<void>(`/crm/clients/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),
  /** Where the CRM and the access model disagree. Read-only. */
  orgDivergences: () =>
    request<{ divergences: OrgDivergence[]; total: number }>('/crm/org-sync'),
  /** Bring the RBAC organisations into line. Dry run unless `apply`. */
  syncOrgs: (apply = false) =>
    request<any>('/crm/org-sync', { method: 'POST', body: JSON.stringify({ apply }) }),

  listCatalog: (catalogId?: string) => {
    const q = catalogId ? `?catalogId=${encodeURIComponent(catalogId)}` : '';
    return request<{ products: CrmProduct[]; total: number; families: any[]; catalogs: string[] }>(
      `/crm/catalog${q}`);
  },
  createProduct: (data: Partial<CrmProduct>) =>
    request<CrmProduct>('/crm/catalog', { method: 'POST', body: JSON.stringify(data) }),
  updateProduct: (id: string, data: Partial<CrmProduct>) =>
    request<CrmProduct>(`/crm/catalog/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  /**
   * Withdraw a SKU from sale. Not a delete: it stops pricing and cannot be
   * added to a quote, but still renders on the quotes that already used it.
   */
  deactivateProduct: (id: string) =>
    request<CrmProduct>(`/crm/catalog/${id}/deactivate`, { method: 'POST' }),
  activateProduct: (id: string) =>
    request<CrmProduct>(`/crm/catalog/${id}/activate`, { method: 'POST' }),
  listRegions: () => request<{ regions: CrmRegion[] }>('/crm/regions'),
  updateRegion: (id: string, data: Partial<CrmRegion>) =>
    request<CrmRegion>(`/crm/regions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  getSettings: () => request<CrmSettings>('/crm/settings'),
  /** List price for one SKU in one region, before discount. Server-side so
   *  pricing arithmetic is never done in the browser. */
  price: (sku: string, region = 'US', quantity = 1) =>
    request<any>(`/crm/price?sku=${encodeURIComponent(sku)}&region=${region}&quantity=${quantity}`),
  /**
   * The whole catalogue as a payload. This is the backup and the transfer
   * mechanism — there is no seed file in the image, because a file that can
   * overwrite live prices is a second source of truth.
   */
  exportCatalog: () => request<Record<string, unknown>>('/crm/catalog/export'),
  /**
   * Restore an export. Dry run unless `apply`. Creates what is missing and
   * leaves what exists alone unless `overwrite` — a restore must not silently
   * revert a price someone corrected.
   */
  restoreCatalog: (payload: unknown, apply: boolean, overwrite = false) => {
    const q = [apply ? 'apply=true' : '', overwrite ? 'overwrite=true' : '']
      .filter(Boolean).join('&');
    return request<any>(`/crm/catalog/restore${q ? `?${q}` : ''}`,
                        { method: 'POST', body: JSON.stringify(payload) });
  },

  // ---- quotes (FE-7) ----
  listQuotes: (opportunityId?: string, sellerOrgId?: string) => {
    const params = new URLSearchParams();
    if (opportunityId) params.set('opportunityId', opportunityId);
    if (sellerOrgId) params.set('sellerOrgId', sellerOrgId);
    const q = params.toString();
    return request<{ quotes: CrmQuote[]; total: number }>(`/crm/quotes${q ? `?${q}` : ''}`);
  },
  getQuote: (id: string) => request<CrmQuote>(`/crm/quotes/${id}`),
  quoteVersions: (id: string) =>
    request<{ quoteNumber: string; versions: CrmQuote[] }>(`/crm/quotes/${id}/versions`),
  /** Price without saving. The editor never computes totals itself — the
   *  discount cap must be applied by the same code that enforces it. */
  previewQuote: (data: any) =>
    request<any>('/crm/quotes/preview', { method: 'POST', body: JSON.stringify(data) }),
  createQuote: (data: any) =>
    request<CrmQuote>('/crm/quotes', { method: 'POST', body: JSON.stringify(data) }),
  /** 201 + newVersion:true when the edit forked an issued quote. */
  updateQuote: (id: string, data: any) =>
    request<{ quote: CrmQuote; newVersion: boolean }>(`/crm/quotes/${id}`,
      { method: 'PUT', body: JSON.stringify(data) }),
  transitionQuote: (id: string, status: string) =>
    request<CrmQuote>(`/crm/quotes/${id}/status`,
      { method: 'PATCH', body: JSON.stringify({ status }) }),
  // ---- documents on an opportunity ----
  listOpportunityDocuments: (opportunityId: string) =>
    request<{ documents: CrmDocument[]; total: number }>(
      `/crm/opportunities/${opportunityId}/documents`),
  /** Multipart, so not through `request()` (it would set a JSON content type).
   *  A signed quote accepts that quote; a PO records its number on it. */
  uploadOpportunityDocument: async (opportunityId: string, file: File,
    fields: { kind: string; quoteId?: string; poNumber?: string }) => {
    const token = await getValidAccessToken();
    const body = new FormData();
    body.append('file', file);
    Object.entries(fields).forEach(([k, v]) => { if (v) body.append(k, v); });
    const res = await fetch(`${BASE_URL}/crm/opportunities/${opportunityId}/documents`,
      { method: 'POST', body, headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      let message = `Upload failed (${res.status})`;
      try { const p = await res.json(); message = p?.error || p?.message || message; } catch { /* keep status */ }
      throw new Error(message);
    }
    return res.json() as Promise<{ document: CrmDocument; effects: string[] }>;
  },

  // ---- invoices ----
  listInvoices: (params?: { opportunityId?: string; quoteId?: string; sellerOrgId?: string }) => {
    const q = new URLSearchParams(
      Object.entries(params || {}).filter(([, v]) => v) as [string, string][]).toString();
    return request<{ invoices: CrmInvoice[]; total: number }>(`/crm/invoices${q ? `?${q}` : ''}`);
  },
  createInvoice: (quoteId: string, data?: { termsDays?: number; notes?: string }) =>
    request<CrmInvoice>('/crm/invoices', { method: 'POST', body: JSON.stringify({ quoteId, ...data }) }),
  updateInvoice: (id: string, data: { termsDays?: number; notes?: string; poNumber?: string }) =>
    request<CrmInvoice>(`/crm/invoices/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  issueInvoice: (id: string, issueDate?: string) =>
    request<CrmInvoice>(`/crm/invoices/${id}/issue`, { method: 'POST', body: JSON.stringify({ issueDate }) }),
  payInvoice: (id: string, data: { paidOn?: string; reference?: string }) =>
    request<CrmInvoice>(`/crm/invoices/${id}/paid`, { method: 'POST', body: JSON.stringify(data) }),
  voidInvoice: (id: string, reason: string) =>
    request<CrmInvoice>(`/crm/invoices/${id}/void`, { method: 'POST', body: JSON.stringify({ reason }) }),
  deleteInvoice: (id: string) => request<void>(`/crm/invoices/${id}`, { method: 'DELETE' }),

  /** Record the client's PO on any version, in place — it changes nothing they were sent. */
  setQuotePo: (id: string, poNumber: string) =>
    request<CrmQuote>(`/crm/quotes/${id}/po`, { method: 'PUT', body: JSON.stringify({ poNumber }) }),
  approveQuote: (id: string) =>
    request<CrmQuote>(`/crm/quotes/${id}/approve`, { method: 'POST' }),
  duplicateQuote: (id: string) =>
    request<CrmQuote>(`/crm/quotes/${id}/duplicate`, { method: 'POST' }),
  exportQuote: (id: string) => request<any>(`/crm/quotes/${id}/export`),
  /** Drafts only — an issued quote is a record. */
  deleteQuote: (id: string) => request<void>(`/crm/quotes/${id}`, { method: 'DELETE' }),
};

// ---- Program Increments ----
export const piApi = {
  list: () => request<any[]>('/program-increments'),
  get: (id: string) => request<any>(`/program-increments/${id}`),
  create: (data: any) => request<any>('/program-increments', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request<any>(`/program-increments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/program-increments/${id}`, { method: 'DELETE' }),
  getSprints: (id: string) => request<any[]>(`/program-increments/${id}/sprints`),
  // FE-2: participating projects — the release generation fan-out set
  getProjects: (id: string) => request<any[]>(`/program-increments/${id}/projects`),
  // F3: fan out one draft release per project per sprint. Idempotent, so this
  // is also how you fill gaps after adding a project to an existing PI.
  generateReleases: (id: string) =>
    request<{ created: number; skipped: number; releaseIds: string[] }>(
      `/program-increments/${id}/generate-releases`, { method: 'POST' },
    ),
  // FE-1: dry-run the calendar before saving; persists nothing
  cadencePreview: (data: any) =>
    request<any>('/program-increments/cadence-preview', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  cadenceDefaults: (releaseTrainId?: string) =>
    request<any>(
      `/program-increments/cadence-defaults${
        releaseTrainId ? `?releaseTrainId=${encodeURIComponent(releaseTrainId)}` : ''
      }`,
    ),
  // Retiming a PI can be refused with 409 when sprints still hold data; force overrides.
  updateForced: (id: string, data: any) =>
    request<any>(`/program-increments/${id}?force=true`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// ---- Sprints ----
export const sprintApi = {
  list: () => request<any[]>('/sprints'),
  get: (id: string) => request<any>(`/sprints/${id}`),
  update: (id: string, data: any) => request<any>(`/sprints/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getWorkItems: (id: string) => request<any[]>(`/sprints/${id}/workitems`),
  start: (id: string) => request<{ sprint: any; incompleteItems: any[] }>(`/sprints/${id}/start`, { method: 'POST' }),
  rollover: (id: string, workItemIds: string[]) => request<{ rolledOver: number }>(`/sprints/${id}/rollover`, { method: 'POST', body: JSON.stringify({ workItemIds }) }),
};

// ---- Releases ----
export const releaseApi = {
  list: (filters?: {
    projectId?: string; sprintId?: string; piId?: string;
    releaseTrainId?: string; status?: string; releaseType?: string;
    from?: string; to?: string; includeClosed?: boolean;
  }) => {
    const q = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '' && v !== false) q.set(k, String(v));
    });
    const qs = q.toString();
    return request<any[]>(`/releases${qs ? `?${qs}` : ''}`);
  },
  // Typed membership — the API rejects anything but feature/bug/security.
  addItems: (id: string, workItemIds: string[]) =>
    request<any>(`/releases/${id}/workitems`, {
      method: 'POST', body: JSON.stringify({ workItemIds }),
    }),
  removeItems: (id: string, workItemIds: string[]) =>
    request<any>(`/releases/${id}/workitems`, {
      method: 'DELETE', body: JSON.stringify({ workItemIds }),
    }),
  // Off-cycle fork: stays bound to the sprint, may be dated midweek.
  standalone: (id: string, data: {
    workItemIds?: string[]; plannedDate?: string; name?: string;
    version?: string; changeType?: string; description?: string;
  }) => request<any>(`/releases/${id}/standalone`, {
    method: 'POST', body: JSON.stringify(data),
  }),
  // release -> work items -> PRs traceability rollup
  changes: (id: string) => request<{
    releaseId: string; version: string; unmergedCount: number;
    changes: any[]; unmerged: any[]; itemsWithoutLinks: string[];
  }>(`/releases/${id}/changes`),
  // F9 governance
  readiness: (id: string) => request<{
    releaseId: string; version: string; ready: boolean; blockingCount: number;
    checks: Array<{
      check: string; label: string; passed: boolean; summary: string;
      waivable: boolean; waived: boolean;
      waiver: { reason: string; userId: string; at: string } | null;
      blocking: Array<{ id: string; displayId: string; title: string; status: string }>;
    }>;
  }>(`/releases/${id}/readiness`),
  lifecycle: (id: string) => request<{
    workflow: { _id: string; name: string; version: number } | null;
    current: string; allowed: string[]; states: any[];
    approvalRequired: boolean; approved: boolean;
  }>(`/releases/${id}/transitions`),
  transition: (id: string, to: string, comment = '') =>
    request<any>(`/releases/${id}/transition`, {
      method: 'POST', body: JSON.stringify({ to, comment }),
    }),
  approve: (id: string, decision: 'approved' | 'rejected', comment = '') =>
    request<any>(`/releases/${id}/approvals`, {
      method: 'POST', body: JSON.stringify({ decision, comment }),
    }),
  waive: (id: string, check: string, reason: string) =>
    request<any>(`/releases/${id}/waivers`, {
      method: 'POST', body: JSON.stringify({ check, reason }),
    }),
  closeEmpty: (data?: { sprintId?: string; asOf?: string }) =>
    request<{ closed: number }>('/releases/close-empty', {
      method: 'POST', body: JSON.stringify(data || {}),
    }),
  get: (id: string) => request<any>(`/releases/${id}`),
  create: (data: any) => request<any>('/releases', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) => request<any>(`/releases/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/releases/${id}`, { method: 'DELETE' }),
  // audience=public excludes internal/embargoed security items entirely (F4).
  getNotes: (id: string, audience: 'internal' | 'public' = 'internal',
             format: 'markdown' | 'html' | 'json' = 'markdown') =>
    request<{ notes: string; audience: string; format: string; stale: boolean; generatedAt: string }>(
      `/releases/${id}/notes?audience=${audience}&format=${format}`,
    ),
  regenerateNotes: (id: string) =>
    request<{ notes: string; publicNotes: string; notesGeneratedAt: string; notesStale: boolean }>(
      `/releases/${id}/notes`, { method: 'POST' },
    ),
};

// Outbox operability (EP-18 FE-3). Depth and dead letters are the two numbers
// that say whether delivery is healthy; a queue is silent by nature.
export const outboxApi = {
  health: () => request<any>('/outbox/health'),
  stats: () => request<any>('/outbox/stats'),
  list: (status?: string, limit = 200) =>
    request<{ items: any[]; total: number }>(
      `/outbox?limit=${limit}${status ? `&status=${encodeURIComponent(status)}` : ''}`),
  retry: (id: string) => request<any>(`/outbox/${id}/retry`, { method: 'POST' }),
  discard: (id: string, reason: string) =>
    request<any>(`/outbox/${id}/discard`,
      { method: 'POST', body: JSON.stringify({ reason }) }),
};

// ── ITIL service operations (PO51-1161 / PO51-1164) ─────────────────────────
//
// The collection name is the ITIL word, matching the API. Nothing here restates
// the lifecycles: `describe()` serves them from the seeded workflow definitions,
// so the board builds its columns from the same source the server transitions
// against and the two cannot disagree about what states exist.

export type ItsmCollection =
  | 'incidents' | 'requests' | 'problems' | 'changes' | 'tasks' | 'events';

export interface ItsmState {
  id: string;
  label: string;
  category: 'todo' | 'in_progress' | 'done' | 'cancelled';
  terminal: boolean;
}

/** customFields.schedule on an opstask: what the harness scheduler fires while it is in progress. */
export interface OpsSchedule {
  verb: string;
  target: string;
  inputs?: Record<string, unknown>;
  every_minutes: number;
  enabled: boolean;
}

export interface OpsScheduleRun {
  id: string;
  slot: number;
  status: string;
  started_at?: string;
  finished_at?: string;
  duration_s?: number;
  error?: string;
}

export interface OpsScheduleCalendar {
  schedule: OpsSchedule | null;
  status: string;
  active: boolean;
  nextSlot: string | null;
  days: { date: string; runs: OpsScheduleRun[]; expected: number; ok: number; failed: number }[];
  runsError: string | null;
  verbs: string[];
  targets: string[];
  minEveryMinutes: number;
}

export interface ItsmTransition {
  id: string;
  name: string;
  to: string;
  available: boolean;
  /** Why it is unavailable. Present so a UI can explain a missing button. */
  blockedBy: string | null;
  /** Lands in a terminal state of the governing workflow. */
  terminal?: boolean;
  /** A terminal move needs an outcome (the close-out rule); an Event's is derived. */
  requiresOutcome?: boolean;
}

/** Filters and sort for the service board (PO46-1280); applied server-side. */
export type ItsmBoardSort = 'age' | 'priority' | 'sla' | 'updated';
export type ItsmSlaFilter = 'breached' | 'at-risk' | 'due';
/** The value the board filters send for "nobody" (no queue / no assignee). */
export const ITSM_UNASSIGNED = '__unassigned__';

export interface ItsmBoardFacets {
  queues: string[];
  /** Display name per stored queue value (a user-group id since EP-27 FE-4). */
  queueNames?: Record<string, string>;
  assignees: string[];
  orgs: { id: string; name: string }[];
}

export interface ItsmTimelineEntry {
  kind: 'activity' | 'comment';
  id: string;
  at: string;
  actor: string;
  action?: string;
  body?: string;
  details?: Record<string, unknown>;
}

export interface ItsmItem {
  _id: string;
  key?: string;
  title: string;
  description?: string;
  status: string;
  workItemType: string;
  projectId: string;
  assignee?: string;
  majorIncident?: boolean;
  outcome?: string;
  outcomeNote?: string;
  changeWindowStart?: string;
  changeWindowEnd?: string;
  failedWindowStart?: string;
  signalSource?: string;
  signalKey?: string;
  relations?: { type: string; targetId: string }[];
  /** The CMDB assets this ticket is about — the real anchor. */
  affectedCIs?: string[];
  /** The resolver queue: a user group id, copied from the affected asset at creation.
   *  A TEAM, not a person. Older tickets may still hold free text. */
  ownerGroup?: string;
  /** The queue's display name, from the board list (the group's name, or the legacy text). */
  ownerGroupName?: string;
  customFields?: Record<string, unknown> & { schedule?: OpsSchedule };
  updatedAt?: string;
  /** ITIL priority (EP-27 FE-6): computed server-side from impact x urgency. */
  priority?: ItsmPriority;
  impact?: ItsmLevel | '';
  urgency?: ItsmLevel | '';
  /** Write-only: the reason a priority differs from the matrix. */
  priorityReason?: string;
  /** Server-owned: the matrix stamp and any override. */
  priorityBasis?: ItsmPriorityBasis;
  /** Server-owned: response/resolution clocks. */
  sla?: ItsmSla;
  slaBreached?: boolean;
  /** Server-owned (PO46-1272): raised with no affected service, so nobody to route to. */
  unassignedService?: boolean;
  /** On a list response: the organisation of the ticket's Operations project. */
  organizationId?: string;
  /** Write-only on raise: the organisation to file under (no project needed). */
  orgId?: string;
  createdAt?: string;
}

export type ItsmLevel = 'high' | 'medium' | 'low';
export type ItsmPriority = 'low' | 'medium' | 'high' | 'critical';

export interface ItsmPriorityBasis {
  impact?: ItsmLevel;
  urgency?: ItsmLevel;
  computed?: ItsmPriority;
  policyVersion?: number;
  source?: 'matrix' | 'override' | 'manual';
  priority?: ItsmPriority;
  reason?: string;
  by?: string;
  at?: string;
}

export interface ItsmSlaClock {
  targetMinutes: number;
  startedAt: string;
  pausedSeconds: number;
  dueAt: string;
  metAt: string;
  breached: boolean;
  breachedAt: string;
  warnedAt?: string;
  breachNotifiedAt?: string;
}

export interface ItsmSla {
  policyVersion?: number;
  priority?: ItsmPriority;
  pausedSince?: string;
  response?: ItsmSlaClock;
  resolution?: ItsmSlaClock;
}

export interface ItsmSlaPolicy {
  version: number;
  appliesTo: string[];
  matrix: Record<ItsmLevel, Record<ItsmLevel, ItsmPriority>>;
  targets: Record<ItsmPriority, { responseMinutes: number; resolutionMinutes: number }>;
  pauseStatuses: string[];
  resolvedStatuses: string[];
  warnAt: number;
}

export interface ItsmRelation {
  type: string;
  targetId?: string;
  targetType?: string;
  targetKey?: string;
  targetTitle?: string;
  sourceId?: string;
  sourceType?: string;
  sourceKey?: string;
  sourceTitle?: string;
  /** The target is in a project the caller cannot see. */
  restricted?: boolean;
}

// ---- Model Training (EP-26) ------------------------------------------------
export interface TrainingProgramme {
  _id: string;
  name: string;
  kind: string;
  organizationId: string;
  active: boolean;
  /** The project holding the programme's work items (campaigns, batches, activities, runs). */
  trainingProjectId?: string;
  /** What the caller may do on this programme (absent in the list view). */
  can?: { view: boolean; operate: boolean; approve: boolean };
  profile: {
    unit?: { name: string; label?: string };
    passMarks?: { mainScoreMin: number; unsafeMax: number; minEvidence?: number; mainScoreName?: string };
    regressionTolerance?: { mainScoreDrop: number; unsafeRise: number };
    depth?: { rowsPerUnit: number; repetition: number };
    aggregation?: string[];
    stores?: Record<string, string>;
    panels?: string[];
    processTemplate?: { key: string; version?: number | null } | null;
  };
}

export interface TrainingStoreStatus {
  role: string;
  db: string;
  state: 'ok' | 'not_provisioned' | 'unreachable' | 'forbidden' | 'no_credentials';
  docCount?: number;
  message?: string;
  pendingTask?: string;
}

export const trainingApi = {
  /** Training programmes the caller can view (server-scoped by grant). */
  programmes: () => request<TrainingProgramme[]>('/training/programs'),
  programme: (id: string) => request<TrainingProgramme>(`/training/programs/${encodeURIComponent(id)}`),
  /** Replace the training profile (FE-2 ST-9). training:approve and a person's own login; validated whole. */
  saveProfile: (id: string, profile: Record<string, any>) =>
    request<TrainingProgramme & { changed: boolean }>(`/training/programs/${encodeURIComponent(id)}/profile`,
      { method: 'PUT', body: JSON.stringify({ profile }) }),
  /** Close a stale open run as lost, with why (FE-14 ST-9); training:approve. */
  markRunLost: (id: string, body: { runId: string; reason: string }) =>
    request<Record<string, any>>(`/training/programs/${encodeURIComponent(id)}/runs/lost`, { method: 'POST', body: JSON.stringify(body) }),
  /** Each process step's design stage and documents (FE-15 ST-4). */
  steps: (id: string) => request<Record<string, any>>(`/training/programs/${encodeURIComponent(id)}/steps`),
  updateStep: (id: string, act: string, body: Record<string, any>) =>
    request<Record<string, any>>(`/training/programs/${encodeURIComponent(id)}/steps/${encodeURIComponent(act)}`, { method: 'POST', body: JSON.stringify(body) }),
  /** Design decisions as approvals (FE-15 ST-3). */
  decisions: (id: string) => request<{ decisions: Record<string, any>[] }>(`/training/programs/${encodeURIComponent(id)}/decisions`),
  raiseDecision: (id: string, body: Record<string, any>) =>
    request<Record<string, any>>(`/training/programs/${encodeURIComponent(id)}/decisions`, { method: 'POST', body: JSON.stringify(body) }),
  recordDecision: (id: string, body: Record<string, any>) =>
    request<Record<string, any>>(`/training/programs/${encodeURIComponent(id)}/decisions/record`, { method: 'POST', body: JSON.stringify(body) }),
  /** The row budget from measured runs (FE-14 ST-7, A251 4.4); without baseModel/gpu, the measured pairs. */
  rowBudget: (id: string, q: Record<string, string | number> = {}) =>
    request<Record<string, any>>(`/training/programs/${encodeURIComponent(id)}/budget?` +
      new URLSearchParams(Object.entries(q).map(([k, v]) => [k, String(v)])).toString()),
  /** Batches ranked by WSJF computed from their data (FE-14 ST-7); POST writes the terms onto them. */
  wsjf: (id: string) => request<Record<string, any>>(`/training/programs/${encodeURIComponent(id)}/wsjf`),
  computeWsjf: (id: string) => request<Record<string, any>>(`/training/programs/${encodeURIComponent(id)}/wsjf`, { method: 'POST' }),
  /** Plan a campaign from the process template; with background, poll job() until done (FE-17 ST-6). */
  createCampaign: (id: string, body: Record<string, any>) =>
    request<Record<string, any>>(`/training/programs/${encodeURIComponent(id)}/campaigns`, { method: 'POST', body: JSON.stringify(body) }),
  job: (id: string, jobId: string) => request<Record<string, any>>(`/training/programs/${encodeURIComponent(id)}/jobs/${jobId}`),
  /** The Programme map (FE-17 ST-5): objective, bundling, stages, and a batch/round x stage grid per campaign. */
  programmeMap: (id: string) => request<Record<string, any>>(`/training/programs/${encodeURIComponent(id)}/map`),
  /** Where the programme's work stands (FE-17 ST-3): per-epic progress, blocked, checked out, approvals, next by WSJF. */
  progress: (id: string) => request<Record<string, any>>(`/training/programs/${encodeURIComponent(id)}/progress`),
  /** One duty group's members (operators, approvers, model-release); PUT needs group:manage. */
  groupMembers: (id: string, duty: string) =>
    request<{ duty: string; members: string[]; canManage: boolean }>(`/training/programs/${encodeURIComponent(id)}/groups/${duty}/members`),
  setGroupMembers: (id: string, duty: string, emails: string[]) =>
    request<{ duty: string; members: string[] }>(`/training/programs/${encodeURIComponent(id)}/groups/${duty}/members`,
      { method: 'PUT', body: JSON.stringify({ emails }) }),
  /** Training tokens on this programme (issued on API Tokens as the training type): own, or all for an approver. */
  trainingTokens: (id: string) => request<Record<string, any>[]>(`/training/programs/${encodeURIComponent(id)}/tokens`),
  revokeTrainingToken: (id: string, tokenId: string) =>
    request<Record<string, any>>(`/training/programs/${encodeURIComponent(id)}/tokens/${encodeURIComponent(tokenId)}`,
      { method: 'DELETE' }),
  /** Propose a profile change (FE-2 ST-10): training:operate, tokens too; approvers decide it in Approvals. */
  proposeProfile: (id: string, profile: Record<string, any>, reason: string) =>
    request<{ taskId: string; key?: string; changed: string[]; request: Record<string, any> }>(
      `/training/programs/${encodeURIComponent(id)}/profile/proposals`, { method: 'POST', body: JSON.stringify({ profile, reason }) }),
  profileProposals: (id: string) => request<Record<string, any>[]>(`/training/programs/${encodeURIComponent(id)}/profile/proposals`),
  /** Recorded profile changes, newest first: who, when, before, after. */
  profileHistory: (id: string) =>
    request<{ at: string; by: string; before: Record<string, any> | null; after: Record<string, any> | null }[]>(
      `/training/programs/${encodeURIComponent(id)}/profile/history`),
  stores: (id: string) =>
    request<{ programId: string; stores: TrainingStoreStatus[] }>(`/training/programs/${encodeURIComponent(id)}/stores`),
  /** A run task joined to its run record `run:<task _id>` (FE-5 ST-4). */
  run: (programId: string, taskId: string) =>
    request<TrainingRunJoin>(`/training/programs/${encodeURIComponent(programId)}/runs/${encodeURIComponent(taskId)}`),
  /** A dashboard read model (FE-4): runs, models, atoms, evidence, registry, teachers, process-map.
   *  `state` is ok, or why the store could not be read (never zeros). */
  dashboard: (programId: string, name: string, stream = '') =>
    request<{ state: string; message?: string; [k: string]: any }>(
      `/training/programs/${encodeURIComponent(programId)}/dashboards/${encodeURIComponent(name)}${stream ? `?stream=${encodeURIComponent(stream)}` : ''}`),
  /** Switch the generation hold: a new version with who and why (FE-14 ST-3). training:approve, own login. */
  setGenerationHold: (programId: string, on: boolean, reason: string) =>
    request<{ recorded: boolean; on: boolean; reason: string; set_by?: string; set_at?: string; id?: string; version: number }>(
      `/training/programs/${encodeURIComponent(programId)}/generation-hold`,
      { method: 'POST', body: JSON.stringify({ on, reason }) }),
  /** Every campaign with its batches and rounds and what each waits on (FE-5 ST-5). */
  pipeline: (programId: string) =>
    request<TrainingPipeline>(`/training/programs/${encodeURIComponent(programId)}/pipeline`),
  campaign: (programId: string, campaignId: string) =>
    request<TrainingCampaignDetail>(
      `/training/programs/${encodeURIComponent(programId)}/campaigns/${encodeURIComponent(campaignId)}`),
  /** The round planner (FE-6 ST-4): order, refresh set, cumulative corpus and cost per round. */
  campaignPlan: (programId: string, campaignId: string) =>
    request<any>(`/training/programs/${encodeURIComponent(programId)}/campaigns/${encodeURIComponent(campaignId)}/plan`),
  /** The unit relationship graph (FE-6 ST-1/ST-3): the draft or a released version, and the versions. */
  unitGraph: (programId: string, version = 'draft') =>
    request<any>(`/training/programs/${encodeURIComponent(programId)}/graph?version=${encodeURIComponent(version)}`),
  reviewEdge: (programId: string, key: string, decision: 'confirmed' | 'rejected', note = '') =>
    request<any>(`/training/programs/${encodeURIComponent(programId)}/graph/review`,
      { method: 'POST', body: JSON.stringify({ key, decision, note }) }),
  releaseGraph: (programId: string, note = '') =>
    request<any>(`/training/programs/${encodeURIComponent(programId)}/graph/release`,
      { method: 'POST', body: JSON.stringify({ note }) }),
  /** Build the draft from the programme's own sources (APTO only), with the coverage report (FE-6 ST-2). */
  buildGraph: (programId: string, dryRun = false) =>
    request<any>(`/training/programs/${encodeURIComponent(programId)}/graph/build`,
      { method: 'POST', body: JSON.stringify({ dryRun }) }),
  graphNext: (programId: string) =>
    request<any[]>(`/training/programs/${encodeURIComponent(programId)}/graph/next`),
  /** Progress across rounds (FE-6 ST-8): each unit's score and unsafe count per round, regressions flagged. */
  campaignRounds: (programId: string, campaignId: string) =>
    request<any>(`/training/programs/${encodeURIComponent(programId)}/campaigns/${encodeURIComponent(campaignId)}/rounds`),
};

/** What an item waits on, from a dry run of its next move's guards — the same check the move makes. */
export interface TrainingWaiting {
  /** gate: a guard refuses · approval: needs one · pending_approval: requested · ready: nothing blocks · done/cancelled: finished */
  kind: 'gate' | 'approval' | 'pending_approval' | 'ready' | 'done' | 'cancelled';
  transition?: string;
  to?: string;
  toLabel?: string;
  gate?: string;
  reason: string;
  requestId?: string;
}

export interface TrainingPipelineNode {
  id: string;
  key?: string;
  title: string;
  kind: 'batch' | 'round';
  index: number;
  status: string;
  statusLabel: string;
  category: string;
  color?: string;
  waiting: TrainingWaiting;
  dependencies: { id: string; key?: string; title?: string; status?: string; done: boolean; checkout?: TrainingCheckout | null }[];
  /** Who holds the item, if anyone (EP-28). */
  checkout?: TrainingCheckout | null;
}

export interface TrainingCheckout { holder: string; email?: string; actsFor?: string | null; since?: string; until: string }

export interface TrainingPipelineCampaign {
  id: string;
  key?: string;
  title: string;
  status: string;
  statusLabel: string;
  category: string;
  pins: Record<string, string | null>;
  waiting: TrainingWaiting;
  batches: TrainingPipelineNode[];
  rounds: TrainingPipelineNode[];
}

export interface TrainingPipeline {
  programId: string;
  campaigns: TrainingPipelineCampaign[];
}

export interface TrainingGateEvent {
  id: string;
  gate?: string;
  passed?: boolean;
  reason?: string;
  at?: string;
  actor?: string;
}

export interface TrainingCampaignDetail extends TrainingPipelineCampaign {
  activities: Record<string, { id: string; key?: string; title: string; status: string; manual?: boolean }[]>;
  runs: { id: string; key?: string; title: string; status: string; parentId: string; stage?: string; cost?: number | null }[];
  gateHistory: Record<string, TrainingGateEvent[]>;
  /** Sum of run costs read from the runs store; `missing` runs could not be read. */
  spend: { total: number; counted: number; missing: number };
  /** ok | partial | unreachable | no_credentials — for the store-backed parts (history, spend). */
  storeState: string;
  messages: string[];
}

export interface TrainingRunJoin {
  taskId: string;
  runId: string;
  /** ok | partial (some reads failed) | unreachable | no_record | no_credentials | error */
  state: string;
  record: { status?: string; stage?: string; purpose?: string; started?: string | null; ended?: string | null; tier?: string; who?: string } | null;
  cost: number | null;
  gateEvents: { id: string; gate?: string; passed?: boolean; reason?: string; at?: string }[];
  models: { id: string; base_model?: string; seed?: number; completed_at?: string; sha256?: string }[];
  messages: string[];
}

// ---- Approvals (EP-26 FE-7) ---------------------------------------------------
/** A role-addressed request: decided by holders of `permission`, never by a named person. */
export interface ApprovalRequest {
  requestId: string;
  status: 'pending' | 'committed' | 'denied' | 'withdrawn' | 'voided' | 'expired' | 'failed' | string;
  to: string;
  permission: string;
  approvals: number;
  required: number;
  requestedBy: string;
  requestedAt: number;
  expiresAt?: number;
  itemId?: string;
  itemKey?: string;
  itemTitle?: string;
  itemType?: string;
  origin?: string;
  projectId?: string;
  /** 'workitem.transition' (a move) or 'authority.action' (an agent action held by PMO). */
  tool?: string;
  /** An agent's done move, filed for a person to close (PMO never lets an agent finish work). */
  agentFinish?: boolean;
  /** A Research evidence file held for example data (tool 'research.evidence', EP-44): masked findings only. */
  evidence?: {
    evidenceId?: string; fileName?: string; subDimension?: string; uploader?: string; researchUrl?: string;
    findings?: { kind?: string; masked?: string; location?: string; why?: string }[];
  };
  reason?: string;
  /** Who asked, in words: an agent session by name, and the person it acts for. */
  requester?: { label: string; session?: string; onBehalfOf?: string; raw?: string };
  /** What the request is about: the item and its place; in a detail read also its
   *  description, checklist and linked pull requests. */
  item?: {
    id: string; key?: string; title?: string; type?: string; status?: string; url?: string;
    chain?: { id: string; type: string; key?: string; title?: string }[];
    description?: string;
    checklist?: { text: string; checked: boolean }[];
    pullRequests?: { repo: string; number: number; url?: string; title?: string; state?: string }[];
    autoCloseOn?: boolean;
  };
  /** Why the project's auto-close did not close an agent-finished item. */
  autoCloseCheck?: string[] | null;
  /** For an authority request: what the agent asked to do, and why it was held. */
  action?: {
    kind?: string; summary?: string; refusal?: string; agentSession?: string;
    channel?: string; workItemId?: string | null;
    governor?: { verdict?: string; reason?: string };
  };
  /** On My requests: who can decide it now, never the requester (hs-pmo/ep19#12). */
  waitingFor?: { permission?: string; project?: string; people: string[]; count: number; youCanDecide: boolean };
  /** What kind of authority it grants (hs-pmo/ep43#10): agent-authority, token-spend, workflow, evidence, operations, training. */
  category?: string;
  /** Waiving a blocking design finding (hs-pmo/ep43#12). */
  designWaiver?: { principleId?: string | null; severity?: string; reason?: string };
  /** A runner over its daily token budget: allow this run, or raise the budget to `amount`. */
  tokenSpend?: { kind: 'allow-run' | 'raise-budget'; amount?: number | null; usedToday?: number | null; budget?: number | null; runnerSessionId?: string; reason?: string };
}

export interface ApprovalVote {
  by: string;
  decision: 'approve' | 'deny';
  reason: string;
  channel: string;
  at: number;
}

export interface ApprovalDetail extends ApprovalRequest {
  votes: ApprovalVote[];
  closedAt?: number | null;
  error?: string | null;
  /** True when the caller may decide it now (holder, not the requester, not excluded, not voted). */
  canDecide?: boolean;
  /** Why the caller may not decide it, when they may not. */
  cannotDecide?: string | null;
  /** The caller made this request (may withdraw it, never approve it). */
  isRequester?: boolean;
  /** For a training round: what it will train and cost (FE-6 ST-4). */
  roundPlan?: { round: any; cost: any; order: any; refreshBasis: string } | null;
}

export interface ApprovalDecision {
  ok: boolean;
  status: string;
  request: ApprovalRequest;
  error?: string;
}

export const approvalsApi = {
  /** Requests the caller may decide now (server-filtered by permission and separation of duties). */
  pending: () => request<ApprovalRequest[]>('/approvals/pending'),
  /** The caller's own open requests: to follow or withdraw, never to approve. */
  mine: () => request<ApprovalRequest[]>('/approvals/mine'),
  get: (id: string) => request<ApprovalDetail>(`/approvals/${encodeURIComponent(id)}`),
  decide: (id: string, decision: 'approve' | 'deny', reason: string) =>
    request<ApprovalDecision>(`/approvals/${encodeURIComponent(id)}/decide`, {
      method: 'POST', body: JSON.stringify({ decision, reason, channel: 'pmo' }),
    }),
  withdraw: (id: string) =>
    request<ApprovalDecision>(`/approvals/${encodeURIComponent(id)}/withdraw`, { method: 'POST' }),
};

/** The ITIL priority matrix and SLA targets (config of record, EP-27 FE-6). */
export const itsmSlaApi = {
  policy: () => request<ItsmSlaPolicy>('/itsm-sla/policy'),
};

export const itsmApi = {
  /** Collections, their states and their transitions — from the seeds. */
  describe: () => request<{
    collections: Record<ItsmCollection, {
      type: string;
      states: ItsmState[];
      transitions: { id: string; from: string[]; to: string; guards: string[] }[];
    }>;
    relationTypes: string[];
  }>('/itsm'),

  list: (collection: ItsmCollection, opts?: {
    projectId?: string; open?: boolean; majorIncident?: boolean; limit?: number;
    offset?: number; sort?: ItsmBoardSort | ''; order?: 'asc' | 'desc' | '';
    orgId?: string; queue?: string; assignee?: string; priority?: string;
    sla?: ItsmSlaFilter | ''; unassignedService?: boolean;
  }) => {
    const p = new URLSearchParams();
    if (opts?.projectId) p.set('projectId', opts.projectId);
    if (opts?.open) p.set('open', 'true');
    if (opts?.majorIncident) p.set('majorIncident', 'true');
    if (opts?.limit) p.set('limit', String(opts.limit));
    if (opts?.offset) p.set('offset', String(opts.offset));
    if (opts?.sort) p.set('sort', opts.sort);
    if (opts?.order) p.set('order', opts.order);
    if (opts?.orgId) p.set('orgId', opts.orgId);
    if (opts?.queue) p.set('queue', opts.queue);
    if (opts?.assignee) p.set('assignee', opts.assignee);
    if (opts?.priority) p.set('priority', opts.priority);
    if (opts?.sla) p.set('sla', opts.sla);
    if (opts?.unassignedService) p.set('unassignedService', 'true');
    const qs = p.toString();
    return request<{
      items: ItsmItem[]; total: number; type: string; offset: number; limit: number;
      sort: string; order: string; facets: ItsmBoardFacets;
    }>(`/itsm/${collection}${qs ? `?${qs}` : ''}`);
  },

  /** Activity and comments on one ticket, oldest first (PO46-1278). */
  timeline: (collection: ItsmCollection, id: string) =>
    request<{ entries: ItsmTimelineEntry[]; total: number }>(
      `/itsm/${collection}/${id}/timeline`),

  create: (collection: ItsmCollection, data: Partial<ItsmItem>) =>
    request<ItsmItem>(`/itsm/${collection}`,
      { method: 'POST', body: JSON.stringify(data) }),

  update: (collection: ItsmCollection, id: string, data: Partial<ItsmItem>) =>
    request<ItsmItem>(`/itsm/${collection}/${id}`,
      { method: 'PUT', body: JSON.stringify(data) }),

  /** A scheduled opstask's runs by day (PO-84 EP-3 FE-4); runs come from harness-api. */
  schedule: (collection: ItsmCollection, id: string, days = 7) =>
    request<OpsScheduleCalendar>(`/itsm/${collection}/${id}/schedule?days=${days}`),

  /** Available moves AND the blocked ones with their reason. */
  transitions: (collection: ItsmCollection, id: string) =>
    request<{
      current: string; category: string; transitions: ItsmTransition[];
      /** From the governing workflow: the states that end the item. */
      terminalStates?: string[];
      /** From the governing workflow: the outcomes a close may record. */
      outcomes?: string[];
      outcome?: string;
    }>(`/itsm/${collection}/${id}/transitions`),

  /**
   * Apply a transition BY NAME. `fields` are written before the move, so an
   * outcome can accompany a close in one call rather than two.
   */
  transition: (collection: ItsmCollection, id: string, transitionId: string,
               fields?: Record<string, unknown>) =>
    request<ItsmItem>(`/itsm/${collection}/${id}/transitions/${transitionId}`,
      { method: 'POST', body: JSON.stringify(fields || {}) }),

  relations: (collection: ItsmCollection, id: string) =>
    request<{ outbound: ItsmRelation[]; inbound: ItsmRelation[] }>(
      `/itsm/${collection}/${id}/relations`),

  link: (collection: ItsmCollection, id: string, type: string, targetId: string) =>
    request<ItsmItem>(`/itsm/${collection}/${id}/relations`,
      { method: 'POST', body: JSON.stringify({ type, targetId }) }),

  unlink: (collection: ItsmCollection, id: string, type: string, targetId: string) =>
    request<ItsmItem>(
      `/itsm/${collection}/${id}/relations`
      + `?type=${encodeURIComponent(type)}&targetId=${encodeURIComponent(targetId)}`,
      { method: 'DELETE' }),
};

// ── CMDB — the owned assets operations hangs off (PO51-1167) ────────────────
//
// A CI is an ASSET, not work: it has no delivery lifecycle and never flows
// backlog → done. Its own type and its own endpoints, deliberately unlike the
// ITIL tickets, which ARE work items.

export interface ConfigItem {
  _id: string;
  name: string;
  ciType: string;
  lifecycleState: string;
  ownerGroup: string;
  description?: string;
  environment?: string;
  attributes?: Record<string, unknown>;
  /** UPSTREAM — the CIs this one needs. Impact reverses these. */
  dependsOn?: string[];
  /** The client this asset belongs to, as a CRM tenancy key. */
  orgId?: string;
  tags?: string[];
  /** Present on a read: the CIs that name this one. */
  dependents?: { _id: string; name: string; ciType: string }[];
  /** Present on create when the CI has no owner and so cannot route. */
  warning?: string;
}

export interface CiImpact {
  ci: { _id: string; name: string } | null;
  /** What it depends on. */
  upstream: { _id: string; name: string; ciType: string; ownerGroup: string }[];
  /** What depends on IT — the blast radius, transitively. */
  downstream: { _id: string; name: string; ciType: string; ownerGroup: string }[];
  impactCount: number;
  /** Who to tell, without a second round of lookups. */
  affectedOwnerGroups: string[];
}

/** A resolver queue: a user group an ownerGroup references (EP-27 FE-4). */
export interface ResolverQueue {
  id: string;
  name: string;
}

export interface CmdbMeta {
  ciTypes: string[];
  lifecycleStates: string[];
  /** The raw stored owner values: group ids, and legacy free text not yet migrated. */
  ownerGroups: string[];
  /** The user groups an owner may reference, by id. */
  queues?: ResolverQueue[];
}

/** A queue's display name: the group's name, else the stored text (a legacy value). */
export function queueName(value: string | undefined, queues: ResolverQueue[] | undefined): string {
  if (!value) return '';
  return (queues || []).find((q) => q.id === value)?.name || value;
}

export interface MyQueueItem extends ItsmItem {
  /** The group (of mine) this ticket's queue resolved to. */
  queueGroupId: string;
  /** The earliest unmet SLA due time, '' when no clock is open. */
  slaNextDue: string;
  createdAt?: string;
}

/** My queue (EP-27 FE-4, PO46-1288): open tickets in the queues I belong to, by SLA. */
export const itsmQueueApi = {
  mine: (groupId?: string) =>
    request<{ queues: (ResolverQueue & { open: number })[]; items: MyQueueItem[]; total: number }>(
      `/itsm-queues/mine${groupId ? `?groupId=${encodeURIComponent(groupId)}` : ''}`),
};

export const cmdbApi = {
  meta: () => request<CmdbMeta>('/cmdb/meta'),
  list: (opts?: { ciType?: string; ownerGroup?: string; q?: string;
                  includeRetired?: boolean }) => {
    const p = new URLSearchParams();
    if (opts?.ciType) p.set('ciType', opts.ciType);
    if (opts?.ownerGroup) p.set('ownerGroup', opts.ownerGroup);
    if (opts?.q) p.set('q', opts.q);
    if (opts?.includeRetired) p.set('includeRetired', 'true');
    const qs = p.toString();
    return request<{ items: ConfigItem[]; total: number }>(
      `/cmdb${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => request<ConfigItem>(`/cmdb/${id}`),
  create: (data: Partial<ConfigItem>) =>
    request<ConfigItem>('/cmdb', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<ConfigItem>) =>
    request<ConfigItem>(`/cmdb/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: string, force = false) =>
    request<void>(`/cmdb/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),
  impact: (id: string) => request<CiImpact>(`/cmdb/${id}/impact`),
  /** Operational work against this asset — "what is happening to this thing". */
  tickets: (id: string) =>
    request<{ items: ItsmItem[]; total: number }>(`/cmdb/${id}/tickets`),
};

/** Contract templates and their versions (PO43-114).
 *
 *  A published version is immutable once used — editing one creates a new
 *  draft. That is what makes `templateVersion` on a signed contract mean
 *  something years later.
 */
export const contractsApi = {
  listTemplates: (params?: { templateId?: string; status?: string }) => {
    const q = new URLSearchParams(
      Object.entries(params || {}).filter(([, v]) => v) as [string, string][]
    ).toString();
    return request<{ items: ContractTemplate[] }>(`/contracts/templates${q ? `?${q}` : ''}`);
  },
  getTemplate: (id: string) => request<ContractTemplate>(`/contracts/templates/${id}`),
  createTemplate: (body: Partial<ContractTemplate>) =>
    request<ContractTemplate>('/contracts/templates', { method: 'POST', body: JSON.stringify(body) }),
  updateTemplate: (id: string, body: Partial<ContractTemplate>) =>
    request<ContractTemplate>(`/contracts/templates/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  newTemplateDraft: (id: string) =>
    request<ContractTemplate>(`/contracts/templates/${id}/draft`, { method: 'POST' }),
  /** `bump` is required and has no default: the minor/major choice is a legal
   *  judgement about whether obligations changed, not a formatting one. */
  /** Organisations with their contracts nested — partners, then each
   *  partner's clients, matching the shape of the agreement chain. */
  byOrganisation: () => request<ContractTree>('/contracts/by-organisation'),
  states: () => request<Record<string, ContractState>>('/contracts/states'),

  /** Upload a document to a slot.
   *
   *  Not through `request()`: that sets a JSON content type, and multipart
   *  needs the browser to set its own boundary. Sending JSON headers with a
   *  FormData body produces a request the server cannot parse.
   */
  uploadDocument: async (contractId: string, slot: string, file: File) => {
    const token = await getValidAccessToken();
    const body = new FormData();
    body.append('file', file);
    const res = await fetch(
      `${BASE_URL}/contracts/${contractId}/documents/${slot}`,
      { method: 'PUT', body, headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) {
      let message = `Upload failed (${res.status})`;
      try {
        const problem = await res.json();
        message = problem?.message || problem?.error || message;
      } catch { /* not JSON; keep the status */ }
      throw new Error(message);
    }
    return res.json();
  },

  placeholderSigned: (contractId: string) =>
    request<WorkItem>(`/contracts/${contractId}/documents/signed/placeholder`,
      { method: 'POST' }),
  generationTargets: () =>
    request<GenerationTargets>('/contracts/generate/targets'),
  generateMaster: (organisationId: string) =>
    request<WorkItem>('/contracts/generate/master', {
      method: 'POST',
      body: JSON.stringify({ organisationId }),
    }),
  generateFamilyLoan: (terms: FamilyLoanTerms) =>
    request<WorkItem>('/contracts/generate/family-loan', {
      method: 'POST',
      body: JSON.stringify({ terms }),
    }),
  generateOrderForm: (quoteId: string) =>
    request<WorkItem>('/contracts/generate/order-form', {
      method: 'POST',
      body: JSON.stringify({ quoteId }),
    }),
  publishTemplate: (id: string, bump: 'minor' | 'major', note = '') =>
    request<ContractTemplate>(`/contracts/templates/${id}/publish`, {
      method: 'POST', body: JSON.stringify({ bump, note }),
    }),
};
